/**
 * Client-side policy portal access detection (aligned with api/src/utils/policyPortalLockState.ts).
 */

export const POLICY_REFERRAL_CODE = 'POLICY_REFERRAL' as const

export const POLICY_NOT_ISSUED_CODE = 'POLICY_NOT_ISSUED' as const

export const LEGACY_MULTI_VEHICLE_CODE = 'LEGACY_MULTI_VEHICLE' as const

export const PORTAL_TEMPORARILY_UNAVAILABLE_CODE = 'POLICY_PORTAL_LOCKED' as const

/** @deprecated Use PORTAL_TEMPORARILY_UNAVAILABLE_CODE */
export const RENEWAL_QUOTE_LOCK_CODE = PORTAL_TEMPORARILY_UNAVAILABLE_CODE

export const PORTAL_POLICY_INACTIVE_CODE = 'POLICY_INACTIVE' as const

export const PORTAL_ALTERATION_REFERRAL_IMMEDIATE_CODE = 'POLICY_ALTERATION_REFERRAL_IMMEDIATE' as const

export const PORTAL_ALTERATION_REFERRAL_REQUIRED_CODE = 'POLICY_ALTERATION_REFERRAL_REQUIRED' as const

export const PORTAL_POLICY_OMITTED_CODE = 'POLICY_NOT_IN_PORTAL' as const

/** Keep in sync with API `policyPortalLockState.ts` PORTAL_CLAIMS_BEFORE_TERM_CODE. */
export const PORTAL_CLAIMS_BEFORE_TERM_CODE = 'POLICY_CLAIMS_BEFORE_TERM_START' as const

/** Keep in sync with API `policyPortalLockState.ts` PORTAL_POLICY_UPDATES_BLOCKED_CODE. */
export const PORTAL_POLICY_UPDATES_BLOCKED_CODE = 'POLICY_UPDATES_BLOCKED' as const

/** Keep in sync with API POLICY_REFERRAL_CUSTOMER_MESSAGE (legacy underwriting referral). */
export const POLICY_REFERRAL_CUSTOMER_MESSAGE =
  'This policy is with our underwriting team for review. Please call the Club 4X4 call centre to discuss your policy. Online changes are not available until this is complete.'

/** Keep in sync with API `policyPortalLockState.ts` PORTAL_TEMPORARILY_UNAVAILABLE_MESSAGE. */
export const PORTAL_TEMPORARILY_UNAVAILABLE_MESSAGE =
  "Online changes are temporarily unavailable for this policy.\n\nPlease contact the Club 4X4 team on 1300 296 296 and we'll help you."

/** Keep in sync with API `policyPortalLockState.ts` PORTAL_POLICY_UPDATES_BLOCKED_MESSAGE. */
export const PORTAL_POLICY_UPDATES_BLOCKED_MESSAGE =
  "We're preparing your policy for renewal, so some online changes are temporarily unavailable.\n\n" +
  "If you need to make an update, please contact the Club 4X4 team on 1300 296 296 and we'll help you."

/** @deprecated Use PORTAL_TEMPORARILY_UNAVAILABLE_MESSAGE */
export const RENEWAL_QUOTE_LOCK_CUSTOMER_MESSAGE = PORTAL_TEMPORARILY_UNAVAILABLE_MESSAGE

export const PORTAL_POLICY_INACTIVE_MESSAGE =
  "This policy is no longer active.\n\nChanges can't be made online. If you need anything, please contact the Club 4X4 team on 1300 296 296 and we'll help you."

export const PORTAL_ALTERATION_REFERRAL_IMMEDIATE_MESSAGE =
  "We need a little more information to complete your update.\n\nWe'll send you an email within 1 business day outlining what's required. Please check your inbox (and junk folder, just in case).\n\nIf you need help in the meantime, you can contact the Club 4X4 team on 1300 296 296 — we're happy to assist.\n\nOnce everything is sorted, you'll be able to make changes online again."

export const PORTAL_ALTERATION_REFERRAL_REQUIRED_MESSAGE =
  "Online changes are temporarily unavailable for this policy.\n\nWe need a little more information to process your update.\n\nPlease check your email for details, or contact the Club 4X4 team on 1300 296 296 and we'll help you."

/** Keep in sync with API `legacyMultiVehiclePolicy.ts` LEGACY_MULTI_VEHICLE_CUSTOMER_MESSAGE. */
export const LEGACY_MULTI_VEHICLE_CUSTOMER_MESSAGE =
  'This policy includes more than one vehicle type and cannot be managed in the online portal. Please call the Club 4X4 team on 1300 296 296.'

/** Portal 403 codes that must not be treated as auth/session failures. */
export const PORTAL_BLOCKED_CODES: Set<string> = new Set([
  POLICY_REFERRAL_CODE,
  POLICY_NOT_ISSUED_CODE,
  LEGACY_MULTI_VEHICLE_CODE,
  PORTAL_TEMPORARILY_UNAVAILABLE_CODE,
  PORTAL_POLICY_INACTIVE_CODE,
  PORTAL_ALTERATION_REFERRAL_IMMEDIATE_CODE,
  PORTAL_ALTERATION_REFERRAL_REQUIRED_CODE,
  PORTAL_CLAIMS_BEFORE_TERM_CODE,
  PORTAL_POLICY_UPDATES_BLOCKED_CODE,
])

export function isPolicyReferralApiError(err: unknown): boolean {
  const e = err as Error & { status?: number; apiBody?: { code?: string } } | undefined
  return e?.status === 403 && e?.apiBody?.code === POLICY_REFERRAL_CODE
}

/** Underwriting referral, portal lock, inactive policy, or legacy multi-vehicle — show call-centre message. */
export function isPolicyPortalBlockedApiError(err: unknown): boolean {
  const e = err as Error & { status?: number; apiBody?: { code?: string } } | undefined
  if (e?.status !== 403) return false
  const c = e?.apiBody?.code
  return c != null && PORTAL_BLOCKED_CODES.has(c)
}

export function getPolicyReferralMessageFromApiError(err: unknown): string {
  const e = err as Error & { apiBody?: { code?: string; message?: string } } | undefined
  const c = e?.apiBody?.code
  switch (c) {
    case PORTAL_TEMPORARILY_UNAVAILABLE_CODE:
      return PORTAL_TEMPORARILY_UNAVAILABLE_MESSAGE
    case PORTAL_POLICY_INACTIVE_CODE:
      return PORTAL_POLICY_INACTIVE_MESSAGE
    case PORTAL_ALTERATION_REFERRAL_IMMEDIATE_CODE:
      return PORTAL_ALTERATION_REFERRAL_IMMEDIATE_MESSAGE
    case PORTAL_ALTERATION_REFERRAL_REQUIRED_CODE:
      return PORTAL_ALTERATION_REFERRAL_REQUIRED_MESSAGE
    case PORTAL_POLICY_UPDATES_BLOCKED_CODE:
      return PORTAL_POLICY_UPDATES_BLOCKED_MESSAGE
    case LEGACY_MULTI_VEHICLE_CODE:
      return LEGACY_MULTI_VEHICLE_CUSTOMER_MESSAGE
    default:
      break
  }
  const m = e?.apiBody?.message
  if (typeof m === 'string' && m.trim() !== '') return m
  return POLICY_REFERRAL_CUSTOMER_MESSAGE
}

export function isReferralBlockedPolicySummary(p: { referralBlocked?: boolean } | null | undefined): boolean {
  return p?.referralBlocked === true
}

export function isPolicyUpdatesBlockedSummary(
  p: { policyUpdatesBlocked?: boolean } | null | undefined
): boolean {
  return p?.policyUpdatesBlocked === true
}

/** Split API multi-paragraph copy into InfoAlert title + description. */
export function splitPolicyUpdatesBlockedAlertCopy(message?: string | null): {
  message: string
  description: string
} {
  const full = (message?.trim() || PORTAL_POLICY_UPDATES_BLOCKED_MESSAGE).trim()
  const parts = full.split(/\n\n+/)
  if (parts.length >= 2) {
    return { message: parts[0]!.trim(), description: parts.slice(1).join('\n\n').trim() }
  }
  const fallbackParts = PORTAL_POLICY_UPDATES_BLOCKED_MESSAGE.split(/\n\n+/)
  return {
    message: parts[0]?.trim() || fallbackParts[0]!.trim(),
    description: (fallbackParts.slice(1).join('\n\n').trim() || fallbackParts[0]!.trim()),
  }
}

export function isPolicyReferralAlterationResponse(body: {
  success?: boolean
  policyReferralBlocked?: boolean
  code?: string
}): boolean {
  return (
    body?.policyReferralBlocked === true ||
    body?.code === POLICY_REFERRAL_CODE ||
    body?.code === PORTAL_ALTERATION_REFERRAL_IMMEDIATE_CODE ||
    body?.code === PORTAL_ALTERATION_REFERRAL_REQUIRED_CODE
  )
}
